import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { validarFormulario } from "../utils/validators";
import { formatearFecha } from "../utils/formatters";
import { createDiagnostico, updateDiagnostico } from "../services/dataService";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import SectionHeader from "../components/ui/SectionHeader";
import Table from "../components/ui/Table";

export default function Diagnostico({ 
  equipos = [], 
  tickets = [],
  diagnosticos = [], 
  setDiagnosticos, 
  agregarAlHistorial, 
  registrarAuditoria,
  onNotify,
  setTab 
}) {
  const { puede, usuario } = useAuth();
  
  const [form, setForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [equipoSeleccionado, setEquipoSeleccionado] = useState(null);
  const [verDetalle, setVerDetalle] = useState(null);
  
  const [formData, setFormData] = useState({
    equipoId: "",
    ticketId: "",
    diagnostico: "",
    trabajo_recomendado: "",
    observaciones: "",
    tiempo_estimado: "",
    fecha: new Date().toISOString().slice(0, 10)
  });
  const [errors, setErrors] = useState({});

  const equipo = (id) => equipos.find(e => String(e.id) === String(id));
  const ticket = (id) => tickets.find(t => String(t.id) === String(id));

  const ticketEntregado = (ticketId) => {
    if (!ticketId) return false;
    const t = ticket(ticketId);
    return t?.estado === "Entregado";
  };

  const ticketsDisponibles = tickets.filter(t => 
    String(t.equipoId) === String(formData.equipoId) && 
    t.estado === "Diagnóstico"
  );

  const filtrados = diagnosticos
    .filter(d => {
      const eq = equipo(d.equipoId);
      if (!eq) return false;
      return eq.serie?.toLowerCase().includes(search.toLowerCase()) ||
             eq.modelo?.toLowerCase().includes(search.toLowerCase()) ||
             eq.cliente?.toLowerCase().includes(search.toLowerCase()) ||
             d.diagnostico?.toLowerCase().includes(search.toLowerCase()) ||
             d.ticketId?.toString().includes(search);
    })
    .slice().sort((a, b) => {
      const fa = new Date(a.fecha);
      const fb = new Date(b.fecha);
      if (isNaN(fa) && isNaN(fb)) return (b.created_at || 0) - (a.created_at || 0);
      if (isNaN(fa)) return 1;
      if (isNaN(fb)) return -1;
      const diff = fb - fa;
      if (diff !== 0) return diff;
      return (b.created_at || 0) - (a.created_at || 0);
    });

  const resetForm = () => {
    setFormData({ equipoId: "", ticketId: "", diagnostico: "", trabajo_recomendado: "", observaciones: "", tiempo_estimado: "", fecha: new Date().toISOString().slice(0, 10) });
    setErrors({}); setEditing(null); setEquipoSeleccionado(null);
  };

  const handleSubmit = async () => {
    if (!puede("crear") && !editing) { onNotify?.("Sin permisos", "error"); return; }
    if (!puede("editar") && editing) { onNotify?.("Sin permisos", "error"); return; }

    const errores = validarFormulario(formData, ['equipoId', 'diagnostico']);
    if (Object.keys(errores).length > 0) { setErrors(errores); onNotify?.("Campos obligatorios faltantes", "warning"); return; }

    const eq = equipo(formData.equipoId);
    if (!eq) { onNotify?.("Equipo no encontrado", "error"); return; }

    try {
      if (editing) {
        await updateDiagnostico({ ...formData, id: editing.id });
        setDiagnosticos(prev => prev.map(d => d.id === editing.id ? { ...formData, id: editing.id } : d));
        registrarAuditoria?.(`Diagnóstico actualizado para ${eq.serie}`, "Diagnóstico");
        onNotify?.(`Diagnóstico actualizado`, "success");
      } else {
        const nuevo = { ...formData, creado_por: usuario?.nombre || "Sistema", estado: "Pendiente", created_at: Date.now() };
        const saved = await createDiagnostico(nuevo);
        setDiagnosticos(prev => [saved, ...prev]);
        registrarAuditoria?.(`Diagnóstico registrado para ${eq.serie}`, "Diagnóstico");
        if (agregarAlHistorial) agregarAlHistorial(eq.id, eq.serie, "Diagnóstico", `Ticket #${formData.ticketId || 'N/A'} - ${formData.diagnostico}`, usuario?.nombre, []);
        onNotify?.(`Diagnóstico registrado para ${eq.serie}`, "success");
      }
    } catch (e) {
      onNotify?.(e.message, "error");
    }
    resetForm(); setForm(false);
  };

  const handleEdit = (d) => {
    if (!puede("editar")) { onNotify?.("Sin permisos", "error"); return; }
    setFormData({ equipoId: d.equipoId, ticketId: d.ticketId || "", diagnostico: d.diagnostico, trabajo_recomendado: d.trabajo_recomendado || "", observaciones: d.observaciones || "", tiempo_estimado: d.tiempo_estimado || "", fecha: d.fecha });
    setEquipoSeleccionado(equipo(d.equipoId)); setEditing(d); setForm(true); setErrors({});
  };

  const handleDelete = (id, serie) => {
    if (!puede("eliminar")) { onNotify?.("Solo admin puede eliminar", "error"); return; }
    if (window.confirm(`¿Eliminar diagnóstico de ${serie}?`)) {
      setDiagnosticos(prev => prev.filter(d => d.id !== id));
      registrarAuditoria?.(`Diagnóstico de ${serie} eliminado`, "Diagnóstico");
      onNotify?.("Eliminado", "info");
    }
  };

  const handleCrearPresupuesto = (d) => {
    if (ticketEntregado(d.ticketId)) {
      onNotify?.("⛔ No se puede crear presupuesto: el equipo ya fue entregado al cliente.", "error");
      return;
    }
    const eq = equipo(d.equipoId);
    sessionStorage.setItem('cardiohard_diag_to_quote', JSON.stringify({
      equipo: eq,
      diagnostico: d,
      ticketId: d.ticketId,
      items_sugeridos: d.trabajo_recomendado ? [{ id: Date.now(), descripcion: d.trabajo_recomendado, cantidad: 1, precio: 0 }] : []
    }));
    if (setTab) setTab("presupuestos");
    onNotify?.("Datos cargados en Presupuestos. Podés editarlos libremente.", "info");
    setVerDetalle(null);
  };

  // ✅ COLUMNAS ACTUALIZADAS (SIN URGENCIA)
  const columnas = ["Equipo", "Ticket #", "Diagnóstico", "Fecha", "Técnico", "Acciones"];
  
  const filas = filtrados.map(d => {
    const eq = equipo(d.equipoId);
    const tk = ticket(d.ticketId);
    return [
      eq ? (<div><strong>{eq.modelo}</strong><br/><code className="code-badge" style={{fontSize:"11px"}}>{eq.serie}</code><br/><span className="text-xs text-muted">{eq.cliente}</span></div>) : "—",
      tk ? <Badge color="primary">#{tk.id}</Badge> : <span className="text-xs text-muted">Sin vincular</span>,
      <span className="text-sm" title={d.diagnostico}>{d.diagnostico?.slice(0, 40)}{d.diagnostico?.length > 40 ? "..." : ""}</span>,
      <span className="text-xs text-muted">{formatearFecha(d.fecha)}</span>,
      d.creado_por || "—",
      <div className="flex gap-1">
        <Btn small variant="default" onClick={() => setVerDetalle(d)}>👁️ Ver</Btn>
        
        {puede("crear") && usuario?.rol === "Administrador" && !ticketEntregado(d.ticketId) && (
          <Btn small variant="success" onClick={() => handleCrearPresupuesto(d)}>💰 Presupuesto</Btn>
        )}
        
        {puede("editar") && <Btn small onClick={() => handleEdit(d)}>✏️</Btn>}
        {puede("eliminar") && <Btn small variant="danger" onClick={() => handleDelete(d.id, eq?.serie)}>🗑️</Btn>}
      </div>
    ];
  });

  return (
    <div>
      <SectionHeader 
        title={`Diagnósticos (${filtrados.length})`} 
        subtitle="Registrá el diagnóstico técnico y vinculalo al Ticket para trazabilidad completa."
        action={puede("crear") && (
          <Btn variant="primary" onClick={() => { setForm(f => !f); if (form) resetForm(); }}>
            {form ? "Cancelar" : "+ Nuevo Diagnóstico"}
          </Btn>
        )}
      />

      {form && (
        <Card className="form-card">
          <p className="form-card__title">{editing ? "✏️ Editar Diagnóstico" : "🆕 Nuevo Diagnóstico Técnico"}</p>
          <div className="grid grid--2 gap-2 mb-2">
            <Select value={formData.equipoId} onChange={e => { setFormData(p => ({...p, equipoId: e.target.value, ticketId: ""})); setEquipoSeleccionado(equipos.find(eq=>eq.id===e.target.value)); setErrors(pr => ({...pr, equipoId: null})); }} error={errors.equipoId} disabled={!!editing}>
              <option value="">Seleccionar equipo *</option>
              {equipos.filter(e => !["Entregado","Fuera de servicio"].includes(e.estado)).map(eq => (<option key={eq.id} value={eq.id}>{eq.serie} · {eq.modelo} ({eq.cliente})</option>))}
            </Select>
            
            <Select value={formData.ticketId} onChange={e => setFormData(p => ({...p, ticketId: e.target.value}))} disabled={!formData.equipoId || !!editing}>
              <option value="">Vincular a Ticket (opcional)</option>
              {ticketsDisponibles.length > 0 ? (
                ticketsDisponibles.map(t => (
                  <option key={t.id} value={t.id}>
                    #{t.id} · {t.prioridad} · {t.tecnico || 'Sin asignar'}
                  </option>
                ))
              ) : (
                <option disabled>No hay tickets en "Diagnóstico" para este equipo</option>
              )}
            </Select>

            <Input value={formData.tiempo_estimado} onChange={e => setFormData(p => ({...p, tiempo_estimado: e.target.value}))} placeholder="Tiempo estimado (ej: 2-3 horas)" />
            <Input value={formData.fecha} onChange={e => setFormData(p => ({...p, fecha: e.target.value}))} type="date" />
            
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">🔍 Diagnóstico Técnico *</label>
              <textarea value={formData.diagnostico} onChange={e => { setFormData(p => ({...p, diagnostico: e.target.value})); setErrors(pr => ({...pr, diagnostico: null})); }} placeholder="Describí detalladamente el problema detectado..." rows={4} className="input w-full" style={{resize:"vertical"}} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">🔧 Trabajo Recomendado</label>
              <textarea value={formData.trabajo_recomendado} onChange={e => setFormData(p => ({...p, trabajo_recomendado: e.target.value}))} placeholder="Reemplazar placa, calibrar sensor, limpieza profunda..." rows={3} className="input w-full" style={{resize:"vertical"}} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">📝 Observaciones</label>
              <textarea value={formData.observaciones} onChange={e => setFormData(p => ({...p, observaciones: e.target.value}))} placeholder="Notas adicionales..." rows={2} className="input w-full" style={{resize:"vertical"}} />
            </div>
          </div>
          {equipoSeleccionado && (<Card className="mb-3" style={{background:"#0f172a",border:"1px solid #334155"}}><p className="text-sm"><strong>{equipoSeleccionado.modelo}</strong> · Serie: <code>{equipoSeleccionado.serie}</code> · Cliente: {equipoSeleccionado.cliente}</p></Card>)}
          <div className="flex gap-2">
            <Btn variant="primary" onClick={handleSubmit}>{editing ? "Actualizar" : "Guardar Diagnóstico"}</Btn>
            <Btn variant="default" onClick={() => { setForm(false); resetForm(); }}>Cancelar</Btn>
          </div>
        </Card>
      )}

      <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Buscar por equipo, serie, ticket o texto..." className="mb-3" />
      <Card><Table cols={columnas} rows={filas} /></Card>

      {/* 👁️ MODAL COMPLETO CON TRAZABILIDAD (SIN URGENCIA) */}
      {verDetalle && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(5px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:"20px"}} onClick={()=>setVerDetalle(null)}>
          <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:"12px",padding:"28px",maxWidth:"750px",width:"100%",maxHeight:"85vh",overflowY:"auto",boxShadow:"0 25px 50px -12px rgba(0,0,0,0.5)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"20px"}}>
              <h3 style={{margin:0,fontSize:"20px",fontWeight:"700",color:"#f8fafc"}}>📋 Detalle Completo del Diagnóstico</h3>
              <button onClick={()=>setVerDetalle(null)} style={{background:"none",border:"none",color:"#94a3b8",fontSize:"24px",cursor:"pointer"}}>×</button>
            </div>
            
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px",marginBottom:"20px"}}>
              <div style={{background:"#0f172a",padding:"12px",borderRadius:"8px",border:"1px solid #334155"}}>
                <p style={{fontSize:"12px",color:"#94a3b8",margin:"0 0 4px"}}>EQUIPO</p>
                <p style={{margin:0,color:"#f1f5f9",fontWeight:"600"}}>{equipo(verDetalle.equipoId)?.modelo || "—"}</p>
                <p style={{margin:"4px 0 0",fontSize:"13px",color:"#cbd5e1"}}>Serie: {equipo(verDetalle.equipoId)?.serie}</p>
                <p style={{margin:"4px 0 0",fontSize:"13px",color:"#cbd5e1"}}>Cliente: {equipo(verDetalle.equipoId)?.cliente}</p>
              </div>
              <div style={{background:"#0f172a",padding:"12px",borderRadius:"8px",border:"1px solid #334155"}}>
                <p style={{fontSize:"12px",color:"#94a3b8",margin:"0 0 4px"}}>TRAZABILIDAD</p>
                <p style={{margin:0,color:"#f1f5f9"}}>Ticket Vinculado: <Badge color="primary">#{verDetalle.ticketId || "N/A"}</Badge></p>
                <p style={{margin:"4px 0 0",fontSize:"13px",color:"#cbd5e1"}}>Fecha: {formatearFecha(verDetalle.fecha)}</p>
                <p style={{margin:"4px 0 0",fontSize:"13px",color:"#cbd5e1"}}>Técnico: {verDetalle.creado_por}</p>
              </div>
            </div>

            <div style={{marginBottom:"16px"}}>
              <p style={{fontSize:"13px",fontWeight:"600",color:"#cbd5e1",marginBottom:"6px"}}>🔍 DIAGNÓSTICO TÉCNICO</p>
              <div style={{background:"#0f172a",padding:"14px",borderRadius:"8px",border:"1px solid #334155",whiteSpace:"pre-wrap",lineHeight:"1.6",color:"#e2e8f0",fontSize:"14px"}}>
                {verDetalle.diagnostico}
              </div>
            </div>

            {verDetalle.trabajo_recomendado && (
              <div style={{marginBottom:"16px"}}>
                <p style={{fontSize:"13px",fontWeight:"600",color:"#cbd5e1",marginBottom:"6px"}}>🔧 TRABAJO RECOMENDADO</p>
                <div style={{background:"#0f172a",padding:"14px",borderRadius:"8px",border:"1px solid #334155",whiteSpace:"pre-wrap",lineHeight:"1.6",color:"#e2e8f0",fontSize:"14px"}}>
                  {verDetalle.trabajo_recomendado}
                </div>
              </div>
            )}

            {verDetalle.observaciones && (
              <div style={{marginBottom:"20px"}}>
                <p style={{fontSize:"13px",fontWeight:"600",color:"#cbd5e1",marginBottom:"6px"}}>📝 OBSERVACIONES</p>
                <div style={{background:"#0f172a",padding:"14px",borderRadius:"8px",border:"1px solid #334155",whiteSpace:"pre-wrap",lineHeight:"1.6",color:"#94a3b8",fontSize:"14px"}}>
                  {verDetalle.observaciones}
                </div>
              </div>
            )}

            <div style={{display:"flex",gap:"12px",justifyContent:"flex-end",marginTop:"20px",borderTop:"1px solid #334155",paddingTop:"16px"}}>
              <Btn variant="default" onClick={()=>setVerDetalle(null)}>Cerrar</Btn>
              
              {puede("crear") && usuario?.rol === "Administrador" && !ticketEntregado(verDetalle.ticketId) && (
                <Btn variant="success" onClick={()=>handleCrearPresupuesto(verDetalle)}>💰 Crear Presupuesto</Btn>
              )}
            </div>
          </div>
        </div>
      )}

      {filtrados.length === 0 && !form && (
        <Card className="text-center py-8">
          <p className="text-muted mb-2">📭 No hay diagnósticos registrados</p>
          {puede("crear") && <Btn variant="primary" onClick={()=>setForm(true)}>+ Crear primer diagnóstico</Btn>}
        </Card>
      )}
    </div>
  );
}